<script setup>
import { computed } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';

const props = defineProps({
    setting: Object,
    services: Object,
    title: String,
    meta_description: String,
    canonical_url: String,
});

const getImageUrl = (image) => {
    // Gallery model getImageUrlAttribute returns full URL if using accessor, 
    // but here we receive raw data or accessor? 
    // Usually Inertia props are serialized. If accessor 'image_url' is appended, use it.
    // If not, construct it. Gallery model has 'image_path'.
    // Let's assume controller passed standard model serialization.
    // Ideally we should use the accessor 'image_url' if appended in model, or construct it.
    // The model code shows getImageUrlAttribute. So if we access 'image_url' in JSON it should be there IF appended or we access it.
    // However, without 'appends', it might not be in JSON.
    // Safer to check both or construct.
    if (image?.image_url) return image.image_url;
    if (image?.image_path) return `/storage/gallery/${image.image_path}`;
    return '/front/img/default-image.jpg';
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(dateString));
};
</script>

<template>
    <FrontLayout 
        :setting="setting" 
        :title="title" 
        :metaDescription="meta_description"
    >
        <Head>
            <link v-if="canonical_url" rel="canonical" :href="canonical_url" />
        </Head>

        <PageHero 
            title="Layanan & Dokumentasi"
            subtitle="Bukti nyata kepuasan pelanggan menggunakan layanan sewa mobil Gotransport Banyuwangi"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: 'Layanan' }
            ]"
        />

        <section class="py-12 md:py-16 bg-gray-50 dark:bg-gray-900 min-h-[600px] transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                
                <div v-if="services.data && services.data.length > 0">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <article 
                            v-for="service in services.data" 
                            :key="service.id" 
                            class="group bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col"
                        >
                            <!-- Image -->
                            <div class="relative overflow-hidden aspect-[4/3] bg-gray-100 dark:bg-gray-700">
                                <Link :href="`/layanan/${service.slug}`" class="block w-full h-full">
                                    <img 
                                        :src="getImageUrl(service)" 
                                        :alt="service.title" 
                                        class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        loading="lazy"
                                    />
                                    <div class="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
                                </Link>
                                <div class="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-orange-600 shadow-sm" v-if="service.category">
                                    {{ service.category }}
                                </div>
                            </div>

                            <!-- Content -->
                            <div class="p-4 flex-1 flex flex-col">
                                <div class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    <i class="fa fa-calendar text-orange-400"></i>
                                    <span>{{ formatDate(service.date_taken || service.created_at) }}</span>
                                </div>

                                <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 leading-snug group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                                    <Link :href="`/layanan/${service.slug}`">{{ service.title }}</Link>
                                </h3>

                                <p class="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 mb-6 flex-1 leading-relaxed">
                                    {{ service.description || 'Klik untuk melihat detail dokumentasi layanan kami.' }}
                                </p>

                                <div class="pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                                    <Link :href="`/layanan/${service.slug}`" class="text-sm font-semibold text-orange-600 dark:text-orange-400 flex items-center gap-1 group/btn">
                                        Lihat Detail <i class="fa fa-arrow-right text-xs transition-transform group-hover/btn:translate-x-1"></i>
                                    </Link>
                                </div>
                            </div>
                        </article>
                    </div>

                    <!-- Pagination -->
                    <div class="mt-12 flex justify-center" v-if="services.links && services.links.length > 3">
                        <div class="flex flex-wrap gap-2 justify-center">
                            <template v-for="(link, key) in services.links" :key="key">
                                <div v-if="link.url === null" class="px-3 py-2 text-sm text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800" v-html="link.label" />
                                <Link v-else class="px-3 py-2 text-sm border rounded-lg transition-all duration-200" 
                                      :class="link.active ? 'bg-orange-600 text-white border-orange-600 shadow-md' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-orange-50 dark:hover:bg-gray-700 hover:text-orange-600 dark:hover:text-orange-400 hover:border-orange-300 dark:hover:border-orange-500'"
                                      :href="link.url" v-html="link.label" />
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else class="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                     <div class="w-20 h-20 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                         <i class="fa fa-camera text-gray-300 dark:text-gray-600 text-3xl"></i>
                     </div>
                     <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Belum Ada Dokumentasi</h3>
                     <p class="text-gray-500 dark:text-gray-400 mb-6">Dokumentasi layanan akan segera diupdate.</p>
                     <Link href="/" class="inline-flex items-center gap-2 px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-full font-bold transition-colors">
                         <i class="fa fa-home"></i>
                         <span>Kembali ke Beranda</span>
                     </Link>
                </div>

            </div>
        </section>
    </FrontLayout>
</template>
